// The landing page.
//
// A faithful build of the "Vibe cloud" design: warm off-white (never pure
// white), Bricolage Grotesque at 800, exactly one blue, and one dark panel that
// keeps its weight precisely because nothing else on the page is dark.
//
// The interactive parts are REAL. The walkthrough copies a prompt an agent can
// actually run, and the use-case picker serves the prompts from
// lib/agent-prompts.ts — the ones pointing at the live llms.txt and the real
// signup steps. Nothing here is mock copy pretending to be a product.
//
// The only door into the product from this page is CONSOLE (in the nav). Admin
// is deliberately absent: it's for exactly one person, and it lives in the
// footer.

import { IconBrandDiscord } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { c, font, radius, shadow, tracking } from "../design/tokens.ts";
import { AGENT_PROMPTS } from "../lib/agent-prompts.ts";
import { CONSOLE_ORIGIN, DISCORD_INVITE, signupHref } from "../lib/hosts.ts";
import { useSession } from "../session/SessionProvider.tsx";

const WALKTHROUGH_PROMPT =
	"Read https://s3p.dev/llms.txt, then deploy this project to s3p and give me the live URL.";

/** What the agent actually does once you paste. Replayed a line at a time. */
const TERMINAL: Array<{ text: string; color: string }> = [
	{ text: "$ npx @s3p/cli deploy", color: c.darkInk },
	{ text: "↳ bucket provisioned · cognito wired · iam scoped", color: c.darkMuted },
	{ text: "✓ bundled 14 files (212 kB)", color: c.darkInk },
	{ text: "✓ database ready (s3://my-app/data)", color: c.darkInk },
	{ text: "✓ live → https://my-app.s3p.dev", color: c.green },
];

const MAX_W = 1120;

type Copy = (t: string) => void;
type Track = (where: string, id?: string) => void;

export function Home() {
	const { client } = useSession();
	const track: Track = (where, id) => {
		void client?.telemetry.track("prompt_copied", { where, ...(id ? { prompt: id } : {}) });
	};
	const copy: Copy = (text) => void navigator.clipboard?.writeText(text);

	return (
		<div style={{ fontFamily: font.sans, color: c.ink, background: c.bg }}>
			<Hero copy={copy} track={track} />
			<AgentFirst />
			<UseCases copy={copy} track={track} />
			<HowItWorks />
			<PricingStrip />
		</div>
	);
}

/* ------------------------------------------------------------------ hero */

function Hero({ copy, track }: { copy: Copy; track: Track }) {
	return (
		<header
			className="s3p-split"
			style={{
				maxWidth: MAX_W,
				margin: "0 auto",
				padding: "56px 32px 72px",
				display: "grid",
				gridTemplateColumns: "1.05fr 0.95fr",
				gap: 56,
				alignItems: "center",
			}}
		>
			<div>
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						border: `1px solid ${c.border}`,
						borderRadius: radius.pill,
						padding: "6px 14px",
						fontSize: 12.5,
						color: c.muted,
						fontFamily: font.mono,
						marginBottom: 24,
					}}
				>
					<span style={{ width: 6, height: 6, borderRadius: "50%", background: c.green }} />
					agent-first · the vibe cloud
				</div>

				<h1
					className="s3p-hero-title"
					style={{
						margin: "0 0 20px",
						fontSize: 76,
						fontWeight: 800,
						letterSpacing: tracking.hero,
						lineHeight: 0.98,
					}}
				>
					Vibe it.
					<br />
					Ship it. <span style={{ color: c.blue }}>Free.</span>
				</h1>

				<p
					style={{
						margin: "0 0 32px",
						color: c.muted,
						fontSize: 17,
						lineHeight: 1.65,
						maxWidth: 440,
					}}
				>
					Your agent just wrote an app. Give it a home in one command — S3 does the hosting,
					the database, and the auth. You do the vibes.
				</p>

				<HeroActions copy={copy} track={track} />

				<div style={{ marginTop: 32, fontFamily: font.mono, fontSize: 12, color: c.faint }}>
					no config · no AWS account · idle apps cost practically nothing
				</div>
			</div>

			<Walkthrough copy={copy} track={track} />
		</header>
	);
}

function HeroActions({ copy, track }: { copy: Copy; track: Track }) {
	const [copied, setCopied] = useState(false);
	const primary = AGENT_PROMPTS[0];

	return (
		<>
			<div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
				<button
					type="button"
					onClick={() => {
						copy(primary?.build(window.location.origin) ?? "");
						track("hero", primary?.id);
						setCopied(true);
						setTimeout(() => setCopied(false), 2200);
					}}
					style={{
						fontFamily: font.sans,
						background: copied ? c.green : c.blue,
						color: "#fff",
						fontSize: 15,
						fontWeight: 700,
						padding: "14px 30px",
						borderRadius: radius.pill,
						border: "none",
						cursor: "pointer",
						transition: "background 150ms",
					}}
				>
					{copied ? "Prompt copied ✓" : "Delegate to your agent →"}
				</button>
				<a
					href={signupHref()}
					style={{
						textDecoration: "none",
						color: c.ink,
						fontSize: 15,
						fontWeight: 700,
						padding: "13px 26px",
						borderRadius: radius.pill,
						border: `1.5px solid ${c.border}`,
					}}
				>
					Create an account yourself
				</a>
			</div>
			<div style={{ marginTop: 14, fontFamily: font.mono, fontSize: 12, color: c.faint }}>
				{copied
					? "now paste it into Claude Code, Cursor, or whatever you vibe with"
					: "copies a ready-made prompt — your agent reads llms.txt and ships"}
			</div>
		</>
	);
}

/** Copy → Paste → Live. The page claims "one command"; this shows the command. */
function Walkthrough({ copy, track }: { copy: Copy; track: Track }) {
	const [step, setStep] = useState(1);
	const [lines, setLines] = useState(0);
	const timer = useRef<ReturnType<typeof setInterval> | null>(null);

	const stop = () => {
		if (timer.current) clearInterval(timer.current);
		timer.current = null;
	};
	useEffect(() => stop, []);

	function play() {
		stop();
		setStep(2);
		setLines(0);
		timer.current = setInterval(() => {
			setLines((n) => {
				if (n >= TERMINAL.length) {
					stop();
					setTimeout(() => setStep(3), 700);
					return n;
				}
				return n + 1;
			});
		}, 550);
	}

	const tabs = [
		{ num: "1", label: "Copy", key: 1 },
		{ num: "2", label: "Paste", key: 2 },
		{ num: "3", label: "Live", key: 3 },
	];

	return (
		<div
			style={{
				background: c.dark,
				borderRadius: radius.card,
				overflow: "hidden",
				boxShadow: shadow.panelStrong,
			}}
		>
			<div style={{ display: "flex", borderBottom: `1px solid ${c.darkBorder}` }}>
				{tabs.map((t) => {
					const sel = step === t.key;
					const done = step > t.key;
					return (
						<button
							type="button"
							key={t.key}
							onClick={() => {
								if (t.key === 2) {
									play();
									return;
								}
								stop();
								setStep(t.key);
							}}
							style={{
								flex: 1,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 8,
								padding: "14px 8px",
								cursor: "pointer",
								border: "none",
								borderBottom: `2px solid ${sel ? c.blue : "transparent"}`,
								background: sel ? c.darkRaised : "transparent",
								fontFamily: font.sans,
							}}
						>
							<span
								style={{
									width: 20,
									height: 20,
									borderRadius: "50%",
									background: done ? c.green : sel ? c.blue : c.darkBorder,
									color: sel || done ? "#fff" : c.darkMuted,
									fontSize: 11,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								{done ? "✓" : t.num}
							</span>
							<span style={{ fontSize: 12.5, fontWeight: 700, color: sel ? c.bg : c.darkMuted }}>
								{t.label}
							</span>
						</button>
					);
				})}
			</div>

			{step === 1 && (
				<div style={{ padding: "24px 26px 26px" }}>
					<div
						style={{
							fontFamily: font.mono,
							fontSize: 11,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							color: c.darkMuted,
							marginBottom: 14,
						}}
					>
						the whole setup is this prompt
					</div>
					<div
						style={{
							fontFamily: font.mono,
							fontSize: 13,
							lineHeight: 1.9,
							color: c.darkInk,
							background: c.darkRaised,
							border: `1px solid ${c.darkBorderRaised}`,
							borderRadius: 12,
							padding: "16px 18px",
							marginBottom: 18,
						}}
					>
						Read <span style={{ color: c.blueOnDark }}>https://s3p.dev/llms.txt</span>, then
						deploy this project to s3p and give me the live URL.
					</div>
					<button
						type="button"
						onClick={() => {
							copy(WALKTHROUGH_PROMPT);
							track("walkthrough");
							play();
						}}
						style={{
							fontFamily: font.sans,
							width: "100%",
							background: c.blue,
							color: "#fff",
							fontSize: 14,
							fontWeight: 700,
							padding: "13px 0",
							borderRadius: radius.pill,
							border: "none",
							cursor: "pointer",
						}}
					>
						Copy it — see what happens next →
					</button>
				</div>
			)}

			{step === 2 && (
				<div
					style={{
						padding: "22px 26px 26px",
						fontFamily: font.mono,
						fontSize: 12.5,
						lineHeight: 2,
						minHeight: 178,
					}}
				>
					<div style={{ color: c.darkMuted, marginBottom: 4 }}>
						› pasted. your agent takes it from here:
					</div>
					{TERMINAL.slice(0, lines).map((l) => (
						<div key={l.text} style={{ color: l.color }}>
							{l.text}
						</div>
					))}
					<span
						style={{
							display: "inline-block",
							width: 7,
							height: 14,
							background: c.blue,
							verticalAlign: "middle",
						}}
					/>
				</div>
			)}

			{step === 3 && (
				<div style={{ padding: "30px 26px", textAlign: "center" }}>
					<div
						style={{
							width: 44,
							height: 44,
							borderRadius: "50%",
							background: c.green,
							color: "#fff",
							fontSize: 20,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 14px",
						}}
					>
						✓
					</div>
					<div
						style={{
							fontSize: 21,
							fontWeight: 800,
							letterSpacing: tracking.h3,
							color: c.bg,
							marginBottom: 6,
						}}
					>
						Live.
					</div>
					<div
						style={{ fontFamily: font.mono, fontSize: 13, color: c.blueOnDark, marginBottom: 20 }}
					>
						https://my-app.s3p.dev
					</div>
					<div
						style={{
							fontFamily: font.mono,
							fontSize: 11.5,
							color: c.darkFaint,
							marginBottom: 18,
						}}
					>
						hosting · database · auth — all on. nothing to configure.
					</div>
					<button
						type="button"
						onClick={() => {
							stop();
							setStep(1);
							setLines(0);
						}}
						style={{
							fontFamily: font.sans,
							background: "transparent",
							color: "#C9C9CF",
							fontSize: 13,
							fontWeight: 700,
							padding: "9px 22px",
							borderRadius: radius.pill,
							border: "1px solid #3A3A45",
							cursor: "pointer",
						}}
					>
						↺ Run it again
					</button>
				</div>
			)}
		</div>
	);
}

/* ---------------------------------------------------------- agent-first */

function AgentFirst() {
	return (
		<section
			style={{
				borderTop: `1px solid ${c.border}`,
				borderBottom: `1px solid ${c.border}`,
				background: c.bgAlt,
			}}
		>
			<div
				className="s3p-split"
				style={{
					maxWidth: MAX_W,
					margin: "0 auto",
					padding: "56px 32px",
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: 56,
					alignItems: "center",
				}}
			>
				<div>
					<h2
						style={{
							margin: "0 0 14px",
							fontSize: 34,
							fontWeight: 800,
							letterSpacing: tracking.h2,
							lineHeight: 1.08,
						}}
					>
						Built for agents.
						<br />
						Friendly to humans.
					</h2>
					<p style={{ margin: 0, color: c.muted, fontSize: 15, lineHeight: 1.65, maxWidth: 400 }}>
						s3p speaks agent natively: one CLI, one SDK, and an{" "}
						<span style={{ fontFamily: font.mono, fontSize: 13 }}>llms.txt</span> that teaches
						your agent the whole platform in one fetch. No dashboards required — but we made a
						nice one anyway.
					</p>
				</div>
				<div
					style={{
						background: c.bg,
						border: `1px solid ${c.border}`,
						borderRadius: radius.panel,
						padding: "22px 24px",
						fontFamily: font.mono,
					}}
				>
					<div
						style={{
							fontSize: 11,
							color: c.faint,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							marginBottom: 12,
						}}
					>
						paste into your agent
					</div>
					<div style={{ fontSize: 13, lineHeight: 1.9, color: c.ink }}>
						Read <span style={{ color: c.blue }}>https://s3p.dev/llms.txt</span>, then deploy
						this project to s3p. Use the database and auth primitives it describes.
					</div>
					<div style={{ marginTop: 16, display: "flex", gap: 8 }}>
						{["CLI", "SDK", "llms.txt"].map((t) => (
							<span
								key={t}
								style={{
									fontSize: 11.5,
									color: c.muted,
									border: `1px solid ${c.border}`,
									borderRadius: radius.pill,
									padding: "4px 12px",
								}}
							>
								{t}
							</span>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

/* ------------------------------------------------------------- use cases */

function UseCases({ copy, track }: { copy: Copy; track: Track }) {
	const [active, setActive] = useState(AGENT_PROMPTS[0]?.id ?? "new");
	const [copied, setCopied] = useState(false);
	const current = AGENT_PROMPTS.find((p) => p.id === active) ?? AGENT_PROMPTS[0];
	const prompt = current?.build(window.location.origin) ?? "";

	return (
		<section id="usecases" style={{ maxWidth: MAX_W, margin: "0 auto", padding: "72px 32px" }}>
			<h2 style={{ margin: "0 0 10px", fontSize: 34, fontWeight: 800, letterSpacing: tracking.h2 }}>
				Pick a door. Copy a prompt.
			</h2>
			<p style={{ margin: "0 0 32px", color: c.muted, fontSize: 15 }}>
				Your agent does the rest. Manual routes below, if you insist.
			</p>

			<div
				className="s3p-cards-3"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr",
					gap: 16,
					marginBottom: 20,
				}}
			>
				{AGENT_PROMPTS.map((p, i) => {
					const sel = p.id === active;
					return (
						<button
							type="button"
							key={p.id}
							onClick={() => {
								setActive(p.id);
								setCopied(false);
							}}
							style={{
								textAlign: "left",
								border: `1.5px solid ${sel ? c.blue : c.border}`,
								borderRadius: radius.card,
								padding: "22px 24px",
								background: sel ? c.blueTint : c.bg,
								cursor: "pointer",
								display: "flex",
								flexDirection: "column",
								gap: 8,
								fontFamily: font.sans,
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<span
									style={{
										fontFamily: font.mono,
										fontSize: 11,
										color: sel ? c.blue : c.faint,
										letterSpacing: "0.08em",
									}}
								>
									{String(i + 1).padStart(2, "0")}
								</span>
								<span
									style={{
										width: 18,
										height: 18,
										borderRadius: "50%",
										border: `1.5px solid ${sel ? c.blue : c.borderStrong}`,
										background: sel ? c.blue : "transparent",
										boxSizing: "border-box",
										boxShadow: sel ? `inset 0 0 0 3.5px ${c.blueTint}` : "none",
									}}
								/>
							</div>
							<div
								style={{
									fontSize: 19,
									fontWeight: 700,
									letterSpacing: tracking.h3,
									color: c.ink,
								}}
							>
								{p.label}
							</div>
							<div style={{ fontSize: 13, lineHeight: 1.55, color: c.muted }}>{p.hint}</div>
						</button>
					);
				})}
			</div>

			<div
				style={{
					background: c.dark,
					borderRadius: radius.card,
					overflow: "hidden",
					boxShadow: shadow.panel,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "16px 24px",
						borderBottom: `1px solid ${c.darkBorder}`,
					}}
				>
					<div
						style={{
							fontFamily: font.mono,
							fontSize: 11.5,
							letterSpacing: "0.08em",
							color: c.darkMuted,
							textTransform: "uppercase",
						}}
					>
						paste into your agent
					</div>
					<button
						type="button"
						onClick={() => {
							copy(prompt);
							track("usecases", active);
							setCopied(true);
							setTimeout(() => setCopied(false), 1500);
						}}
						style={{
							fontFamily: font.sans,
							fontSize: 13.5,
							fontWeight: 700,
							color: c.ink,
							background: copied ? c.green : c.bg,
							border: "none",
							borderRadius: radius.pill,
							padding: "9px 22px",
							cursor: "pointer",
						}}
					>
						{copied ? "Copied!" : "Copy prompt"}
					</button>
				</div>
				<pre
					style={{
						margin: 0,
						padding: "24px 26px",
						fontFamily: font.mono,
						fontSize: 13,
						lineHeight: 1.9,
						color: c.darkInk,
						minHeight: 96,
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
					}}
				>
					{prompt}
				</pre>
			</div>
		</section>
	);
}

/* ---------------------------------------------------------- how it works */

const PRIMITIVES = [
	{
		key: "s3://app/public",
		title: "Hosting",
		body: "Static-first delivery straight from the bucket. Eleven nines of durability, no cold starts, no servers to wake up.",
	},
	{
		key: "s3://app/data",
		title: "Database",
		body: "JSON documents on S3 with conditional writes. Your agent reads and writes it like a filesystem — because it is one.",
	},
	{
		key: "cognito + iam",
		title: "Auth",
		body: "Cognito sign-in mints per-user IAM credentials. Access control is enforced by AWS itself — not by code you have to write.",
	},
];

function HowItWorks() {
	return (
		<section style={{ borderTop: `1px solid ${c.border}`, background: c.bgAlt }}>
			<div style={{ maxWidth: MAX_W, margin: "0 auto", padding: "72px 32px" }}>
				<h2
					style={{
						margin: "0 0 10px",
						fontSize: 34,
						fontWeight: 800,
						letterSpacing: tracking.h2,
					}}
				>
					One primitive. Three jobs.
				</h2>
				<p style={{ margin: "0 0 36px", color: c.muted, fontSize: 15, maxWidth: 560 }}>
					Almost all compute moves to the browser. S3 holds everything else, and per-user IAM is
					the access-control gate. We run the infrastructure — no AWS account to bring.
				</p>
				<div
					className="s3p-cards-3"
					style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}
				>
					{PRIMITIVES.map((p) => (
						<div
							key={p.key}
							style={{
								background: c.bg,
								border: `1px solid ${c.border}`,
								borderRadius: radius.card,
								padding: 26,
							}}
						>
							<div
								style={{
									fontFamily: font.mono,
									fontSize: 12,
									color: c.blue,
									marginBottom: 12,
								}}
							>
								{p.key}
							</div>
							<div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{p.title}</div>
							<p style={{ margin: 0, color: c.muted, fontSize: 13.5, lineHeight: 1.6 }}>
								{p.body}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

/* -------------------------------------------------------------- pricing */

function PricingStrip() {
	return (
		<section style={{ borderTop: `1px solid ${c.border}` }}>
			<div
				style={{ maxWidth: MAX_W, margin: "0 auto", padding: "72px 32px", textAlign: "center" }}
			>
				<h2
					style={{
						margin: "0 0 14px",
						fontSize: 44,
						fontWeight: 800,
						letterSpacing: tracking.hero,
					}}
				>
					Free means <span style={{ color: c.blue }}>free</span>.
				</h2>
				<p
					style={{
						margin: "0 auto 30px",
						color: c.muted,
						fontSize: 15,
						maxWidth: 460,
						lineHeight: 1.6,
					}}
				>
					Deploy unlimited prototypes on the free tier. Pay only when one of them turns into a
					real thing — and even then, it's S3 pricing, not platform pricing.
				</p>
				<div
					style={{
						display: "flex",
						gap: 14,
						justifyContent: "center",
						alignItems: "center",
						flexWrap: "wrap",
					}}
				>
					{/* → SIGN UP, not the console.
					    This pointed at CONSOLE_ORIGIN, and the console now redirects a
					    signed-out visitor straight to /signin. So the landing page's
					    primary CTA — the one aimed squarely at people who have never heard
					    of us — opened a LOGIN FORM. Asking a stranger for a password to an
					    account they don't have is the fastest way to lose them. Send them
					    where the button says: the page that makes the account and the app.
					    (Already signed in? CreateApp skips the account step by itself.) */}
					<a
						href={signupHref()}
						style={{
							textDecoration: "none",
							background: c.blue,
							color: "#fff",
							fontSize: 15,
							fontWeight: 700,
							padding: "14px 30px",
							borderRadius: radius.pill,
						}}
					>
						Deploy something →
					</a>
					<Link to="/pricing" style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>
						see pricing
					</Link>
				</div>

				{/* The community lives NEXT to the primary CTA, not buried in the footer:
				    the people who read all the way down here are exactly the ones who'd
				    join, and it's a much cheaper first step than deploying. A secondary
				    link, though — it must never compete with "Deploy something". */}
				<a
					href={DISCORD_INVITE}
					target="_blank"
					rel="noreferrer noopener"
					style={{
						marginTop: 26,
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						textDecoration: "none",
						color: c.muted,
						fontSize: 14,
						fontWeight: 600,
						border: `1px solid ${c.border}`,
						background: c.bg,
						padding: "9px 16px",
						borderRadius: radius.pill,
					}}
				>
					<IconBrandDiscord size={17} color={c.blue} />
					Join the community on Discord
				</a>

				<div style={{ marginTop: 22, fontFamily: font.mono, fontSize: 12, color: c.faint }}>
					no card. no cold starts. no kidding.
				</div>
			</div>
		</section>
	);
}
